"use client";

import { useEffect } from "react";
import { useCore } from "@/core-nextv3/core/core";
import { useFacebookHashedUserData } from "@/core-nextv3/Hooks/useFacebookHashedUserData";
import { setFbcFromFbclid } from "@/core-nextv3/util/util";

declare const window: any;
let gtagScriptPromise: Promise<void> | null = null;

interface FacebookPixelOptions {
    pixelId: string;
    userData?: Record<string, string>; 
  }

export function useFacebookPixel({ pixelId, userData }: FacebookPixelOptions) 
{  
    useEffect(() => 
    {        
        if (!pixelId) return;
  
        // Define stub do fbq se ainda não existe
        if (typeof window.fbq !== "function") 
        {
            window.fbq = function (...args: any[]) 
            {
                (window.fbq as any).callMethod
                    ? (window.fbq as any).callMethod(...args)
                    : (window.fbq as any).queue.push(args);
            };
  
            window.fbq.queue = [];
            window.fbq.loaded = true;
            window.fbq.version = "2.0";
        }
  
        const initPixel = () => 
        {  
            const currentState = (window.fbq as any)?.getState?.();
            const alreadyInitialized = currentState?.pixels?.some(
                (p: any) => p?.id === pixelId
            );
  
            if (!alreadyInitialized) 
            {
                window.fbq("init", pixelId);
            }
  
            if (userData) 
            {
                window.fbq("set", "userData", userData);
            }
        };
  
        // Carrega script do Meta Pixel, se necessário
        if (!document.querySelector("script[src*=\"fbevents.js\"]")) 
        {
            const script = document.createElement("script");
            script.async = true;
            script.src = "https://connect.facebook.net/en_US/fbevents.js";
  
            script.onload = () => 
            {
                // Aguarda fbq estar pronto de verdade
                if (typeof window.fbq === "function") 
                {
                    initPixel();
                }
            };
  
            document.head.appendChild(script);
        }
        else 
        {
            initPixel();
        }
    }, [ pixelId, JSON.stringify(userData) ]);
}
  
export const loadGtagScript = (id: string, hashedUserData:any): Promise<void> => 
{
    if (typeof window === "undefined" || !id) return Promise.resolve();

    // Se já estiver carregando ou carregado, reutiliza a mesma Promise
    if (gtagScriptPromise) return gtagScriptPromise;

    // Se gtag já existe no window, considera carregado
    if (typeof window.gtag === "function") return Promise.resolve();

    gtagScriptPromise = new Promise((resolve, reject) => 
    {
        const script = document.createElement("script");
        script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
        script.async = true;

        script.onload = () => 
        {
            window.dataLayer = window.dataLayer || [];

            window.gtag = function () 
            {
                window.dataLayer.push(arguments);
            };

            window.gtag("js", new Date());

            window.gtag("set", {
                user_properties : {
                    external_id : hashedUserData?.externalId,
                }
            });

            window.gtag("config", id, {
                debug_mode    : false,
                user_id       : hashedUserData?.externalId,
                page_path     : window.location.pathname,
                page_location : window.location.href,
                page_title    : document.title,
            });

            window.__gtagInitialized = true;

            resolve();
        };

        script.onerror = () => 
        {
            console.error("[gtag] Falha ao carregar o script.");
            reject(new Error("Erro ao carregar gtag.js"));
        };

        document.head.appendChild(script);
    });

    return gtagScriptPromise;
};

export default function AnalyticsProvider({ account }: any) 
{
    const { user } = useCore();
    const { hashedData } = useFacebookHashedUserData(user);

    setFbcFromFbclid();

    useFacebookPixel({
        pixelId  : account?.fb?.id,
        userData : hashedData,
    });

    useEffect(() => 
    {
        if (account?.ga?.id) 
        {
            loadGtagScript(account.ga.id, hashedData).catch((err) =>
                console.error("[AnalyticsProvider] Erro ao carregar GA4:", err)
            );
        }
    }, [ account?.ga?.id ]);

    return (
        <noscript>
            <img
                height="1"
                width="1"
                style={{ display : "none" }}
                src={`https://www.facebook.com/tr?id=${account?.fb?.id}&ev=PageView&noscript=1`}
            />
        </noscript>
    );
}